import { useEffect } from "react";
import { Alert, AlertTitle } from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";
import { removeNotification } from "shared/slices/statusNotificationsSlice";
import { useTranslation } from "react-i18next";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { NotificationErrorIconSvg } from "components/icons/NotificationErrorIconSvg";
import { NotificationInfoIconSvg } from "components/icons/NotificationInfoIconSvg";
import { NotificationSuccessIconSvg } from "components/icons/NotificationSuccessIconSvg";
import { NotificationWarningIconSvg } from "components/icons/NotificationWarningIconSvg";

export function NotificationPopup() {
    const notifications = useSelector((state: RootState) => state.statusNotificationsSlice.notificationList);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const titles: Record<string, string> = {
        Error: t("Unsuccessful"),
        Success: t("Successful"),
        Warning: t("Warning"),
        Info: t("Info"),
    };

    const handleClose = (id: string) => {
        dispatch(removeNotification(id));
    };

    // Base duration for short messages; longer ones stay on screen long enough to be read.
    const BASE_DURATION = 4000;
    const BASE_CHAR_LIMIT = 300;
    const READING_CHARS_PER_SECOND = 20; // ~240 words per minute for an average reader
    const MAX_DURATION = 20000;

    const getNotificationDuration = (message: string) => {
        const length = message.length;
        if (length <= BASE_CHAR_LIMIT) {
            return BASE_DURATION;
        }
        const readingDuration = (length / READING_CHARS_PER_SECOND) * 1000;
        return Math.min(Math.max(readingDuration, BASE_DURATION), MAX_DURATION);
    };

    const getNotificationIcon = (type: NotificationTypes) => {
        switch (type) {
            case NotificationTypes.Success:
                return <NotificationSuccessIconSvg />;

            case NotificationTypes.Error:
                return <NotificationErrorIconSvg />;

            case NotificationTypes.Info:
                return <NotificationInfoIconSvg />;

            case NotificationTypes.Warning:
                return <NotificationWarningIconSvg />;

            default:
                return null;
        }
    };

    useEffect(() => {
        const timers = notifications.map(notification =>
            setTimeout(() => {
                dispatch(removeNotification(notification.id));
            }, getNotificationDuration(t(notification.message)))
        );

        return () => timers.forEach(clearTimeout);
    }, [notifications, dispatch]);

    return (
        <div className="notifications-container">
            {notifications.map(notification => (
                <Alert
                    id={`Notification_Message_${notification.id}`}
                    role="Notification_Message"
                    key={notification.id}
                    className={`notification-div ${notification.type}`}
                    sx={{ "& .MuiAlert-message": { position: "relative", left: "-23px" } }}
                    onClose={() => handleClose(notification.id)}
                    icon={getNotificationIcon(notification.type)}
                    variant="filled">
                    {titles[notification.type] && <AlertTitle className="notification-title">{titles[notification.type]}</AlertTitle>}
                    <div>{t(notification.message)}</div>
                </Alert>
            ))}
        </div>
    )
}